import { useCallback, useEffect, useMemo } from 'react';
import { useApiUrl } from '../../global/config/useApiUrl';
import { useCampfireFetch } from '../../global/network/useCampfireFetch';

export type CapacityStatus = 'FULL' | 'ALMOST_FULL' | 'SPOTS_OPEN' | 'IN_NEED' | null;

export interface SessionCapacityStatus {
  sessionId: string;
  sessionName: string;
  status: CapacityStatus;
  minVolunteers: number;
  maxVolunteers: number | null;
  rosteredVolunteers: number;
  availableSpots: number | null;
}

export interface ActivityDateCapacityStatus {
  activityDate: string;
  overallStatus: CapacityStatus;
  sessions: SessionCapacityStatus[];
}

interface ActivityCapacityRequest {
  activityId: string;
  dates: string[];
}

interface CapacityStatusResponse {
  activities: Record<string, ActivityDateCapacityStatus[]>;
}

export function useActivityCapacityStatus(activities: ActivityCapacityRequest[]) {
  const apiUrl = useApiUrl();
  const campfireFetch = useCampfireFetch<CapacityStatusResponse>({ defer: true });

  const activitiesKey = useMemo(() => JSON.stringify(activities), [activities]);

  useEffect(() => {
    if (activities.length === 0) {
      return;
    }

    campfireFetch.run({
      url: `${apiUrl}/vm/activity/capacity-status`,
      method: 'POST',
      data: { activities },
    });
  }, [activitiesKey, apiUrl]);

  const data = useMemo(() => {
    if (!campfireFetch.response?.data?.data?.activities) {
      return {};
    }
    return campfireFetch.response.data.data.activities;
  }, [campfireFetch.response]);

  const getStatusForActivityDate = useCallback(
    (activityId: string, date: string): ActivityDateCapacityStatus | undefined => {
      const activityDates = data[activityId];
      if (!activityDates) return undefined;
      return activityDates.find((d) => d.activityDate === date);
    },
    [data]
  );

  const getStatusForSession = useCallback(
    (activityId: string, date: string, sessionId: string): SessionCapacityStatus | undefined => {
      const dateStatus = getStatusForActivityDate(activityId, date);
      if (!dateStatus) return undefined;
      return dateStatus.sessions.find((s) => s.sessionId === sessionId);
    },
    [getStatusForActivityDate]
  );

  return {
    data,
    isLoading: campfireFetch.isLoading,
    error: campfireFetch.error,
    getStatusForActivityDate,
    getStatusForSession,
  };
}
